import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { DocumentMetaService } from '@qtek/libs/store';
import { DocumentType, SchemaProperty } from '@qtek/shared/models';
import { NEVER, Observable, map, merge, of, race, timer } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import {
  buildConflictReview,
  ConflictRow,
  FieldSelection,
  parseFieldValue,
} from '../benchmark-publish-dialog/credit-agreement-extraction.utils';

/** Mirrors `ExtractorState` in `@qtek/libs/data-room`, duplicated to avoid a lib cycle. */
export type ExtractionProgress = 'pending' | 'done' | 'failed';

export interface ExtractionReviewDialogData {
  /** The extracted document — `document._id`, not the reference id. */
  documentId: string;
  /** Decides the wait budget; credit agreements are the slowest documents. */
  documentType: DocumentType;
  /**
   * Live state of the extractor that produces this document's fields. When supplied,
   * it — not the clock — decides when to stop waiting. Optional so the dialog still
   * works standalone, falling back to {@link EXTRACTION_TIMEOUT_MS}.
   */
  progress$?: Observable<ExtractionProgress>;
  /** Current deal values, keyed by dotted schema path. */
  currentValues: Record<string, unknown> | null;
  /** Flat schema properties keyed by the same paths, for units and formatting. */
  properties?: Record<string, SchemaProperty> | null;
  fileName?: string;
}

/**
 * Deal-form paths the user chose to take from the document, with values already
 * parsed for the form (percents become decimals, money becomes numbers). Matches
 * what `benchmark-publish` returns, so callers apply both the same way.
 */
export type ExtractionReviewResult = Record<string, unknown>;

/**
 * Sized to match `termSheetReviewDialogConfig`, the other surface where a user reviews
 * extracted fields. Deliberately not `disableClose`: unlike the term-sheet flow, closing
 * this one is a supported action — extraction continues and the review can be reopened
 * from the document menu.
 */
export const extractionReviewDialogConfig: MatDialogConfig = {
  autoFocus: false,
  panelClass: ['qt-dialog', 'extraction-review-dialog'],
  width: '860px',
  maxWidth: '95vw',
  maxHeight: '90vh',
};

/**
 * Fallback budget, used only when no {@link ExtractionReviewDialogData.progress$} is
 * supplied. A wall clock is the wrong instrument here — it measures time since the
 * user consented, while the work itself may sit queued behind unrelated executors
 * for minutes (SD-2738). A credit agreement's 180s was already exceeded that way by
 * queueing alone, which is why progress supersedes it when available.
 */
const EXTRACTION_TIMEOUT_MS: Partial<Record<DocumentType, number>> = {
  [DocumentType.CREDIT_AGREEMENT]: 180_000,
  [DocumentType.TERM_SHEET]: 120_000,
};
const DEFAULT_EXTRACTION_TIMEOUT_MS = 180_000;

/**
 * How long to keep waiting for metadata after the extractor reports it finished.
 * Covers the hop from the extractor's commit to the websocket broadcast; past this
 * the extractor genuinely produced nothing for us.
 */
const POST_EXTRACTION_GRACE_MS = 15_000;

/**
 * Absolute ceiling when progress is being tracked, so a stalled or never-scheduled
 * extractor can't leave the dialog spinning forever. Deliberately far above any
 * legitimate queue wait — it is a backstop, not a budget.
 */
const PROGRESS_BACKSTOP_MS = 900_000;

type DocumentMetaLike = {
  dataType?: string;
  containers?: { data?: unknown }[];
};

/**
 * Picks the field-extraction metadata out of a websocket frame, or null if this
 * frame doesn't carry it yet.
 *
 * <p>Three things make this less direct than it looks. The subscription opens with
 * `cmd: 'query'`, so the first frame's `res` is an **array** of existing rows, while
 * later CREATE events carry a single object — both shapes have to be accepted.
 *
 * <p>A document also produces more than one metadata row: TermSheetExtractor or
 * CreditAgreementExtractor write the deal fields, and CovenantExtractor writes
 * covenant rows separately, arriving up to a minute later. Only the field rows are
 * reviewable here, and the covenant row carries neither `isValid` nor field
 * containers — taking whichever arrived first reported a perfectly good extraction
 * as unreadable.
 *
 * <p>Returning null rather than a default keeps the caller waiting: an early frame
 * with nothing matching is not the same as extraction having failed.
 */
function fieldExtractionMeta(message: unknown): DocumentMetaLike | null {
  const res = (message as { res?: unknown })?.res;
  const rows: DocumentMetaLike[] = Array.isArray(res)
    ? (res as DocumentMetaLike[])
    : res
      ? [res as DocumentMetaLike]
      : [];

  return (
    rows.find(
      row =>
        row?.dataType === 'TERM_SHEET' || row?.dataType === 'CREDIT_AGREEMENT'
    ) ?? null
  );
}

/**
 * `unreadable` and `timedOut` are deliberately distinct. `unreadable` is the
 * extractor's own verdict on the document; `timedOut` means we simply never heard
 * back, which says nothing about the document and is usually a delivery problem on
 * our side. Collapsing them told a user their perfectly good credit agreement
 * couldn't be read, and cost a database query to disprove.
 */
type ReviewState =
  | 'waiting'
  | 'ready'
  | 'unreadable'
  | 'timedOut'
  | 'nothingToReview';

/**
 * SD-2665 — review extracted values against the deal and choose which to keep.
 *
 * <p>Opens as soon as extraction is requested and waits: extraction is a
 * multi-minute LLM pass with no synchronous result, so the dialog subscribes to
 * the document's `DocumentMeta` and races it against a timeout. The user can
 * close it and come back — the extraction continues regardless, since it is the
 * backend that owns it.
 *
 * <p>Deliberately dumb about the deal. Current values and schema properties are
 * passed in, and the chosen values are returned rather than written here, so the
 * dialog has no opinion on how a deal is loaded or saved and can be tested with
 * plain objects.
 */
@Component({
  selector: 'qt-extraction-review-dialog',
  templateUrl: './extraction-review-dialog.component.html',
  styleUrls: ['./extraction-review-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    TranslationCoreModule,
  ],
})
export class ExtractionReviewDialogComponent {
  private readonly dialogRef =
    inject<
      MatDialogRef<
        ExtractionReviewDialogComponent,
        ExtractionReviewResult | null
      >
    >(MatDialogRef);
  private readonly data = inject<ExtractionReviewDialogData>(MAT_DIALOG_DATA);
  private readonly documentMetaService = inject(DocumentMetaService);
  private readonly destroyRef = inject(DestroyRef);

  readonly state = signal<ReviewState>('waiting');
  readonly rows = signal<ConflictRow[]>([]);
  readonly fileName = this.data.fileName ?? '';

  /** Drives the running count and gates Save — nothing taken means nothing to write. */
  readonly selectedFromDocumentCount = computed(
    () => this.rows().filter(row => row.selection === 'document').length
  );

  constructor() {
    this.awaitExtraction();
  }

  private awaitExtraction(): void {
    const mysid = `extraction_review_${this.data.documentId}`;

    const meta$ = this.documentMetaService
      .subscribeDocumentMeta(mysid, this.data.documentId)
      .pipe(
        map(message => fieldExtractionMeta(message)),
        filter(Boolean),
        take(1)
      );

    race(meta$, this.giveUp$())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(outcome => {
        if (typeof outcome === 'string') {
          this.state.set(outcome);
          return;
        }
        this.handleExtraction(outcome);
      });
  }

  /**
   * Emits the state to fall back to once waiting is pointless — and, crucially,
   * emits nothing at all while the extractor is still queued or running.
   *
   * <p>With progress available, "we waited long enough" is no longer a judgement about
   * elapsed time: the extractor finishing without metadata is the real failure signal,
   * and everything before that is just waiting. The backstop only exists so a stalled
   * extractor can't hold the dialog open indefinitely.
   */
  private giveUp$(): Observable<ReviewState> {
    if (!this.data.progress$) {
      const timeoutMs =
        EXTRACTION_TIMEOUT_MS[this.data.documentType] ??
        DEFAULT_EXTRACTION_TIMEOUT_MS;
      return timer(timeoutMs).pipe(map((): ReviewState => 'timedOut'));
    }

    const settled$ = this.data.progress$.pipe(
      switchMap((progress): Observable<ReviewState> => {
        if (progress === 'failed') {
          return of('unreadable');
        }
        if (progress === 'done') {
          return timer(POST_EXTRACTION_GRACE_MS).pipe(
            map((): ReviewState => 'unreadable')
          );
        }
        return NEVER;
      })
    );

    return merge(
      settled$,
      timer(PROGRESS_BACKSTOP_MS).pipe(map((): ReviewState => 'timedOut'))
    );
  }

  private handleExtraction(meta: { containers?: { data?: unknown }[] }): void {
    const extracted = meta.containers?.[0]?.data as
      | Record<string, string | null>
      | undefined;

    // `isValid` is the extractor's own verdict on whether it could read the
    // document at all — a scanned PDF yields no text and lands here. Until
    // SD-2670/SD-2671 we cannot tell a scan from any other failure, so the
    // message stays neutral.
    if (!extracted || extracted['isValid'] !== 'true') {
      this.state.set('unreadable');
      return;
    }

    const review = buildConflictReview({
      extracted,
      currentValues: this.data.currentValues,
      properties: this.data.properties,
    });

    // Extraction succeeded but agreed with the deal on everything it found.
    // Distinct from unreadable: nothing is wrong, there is simply nothing to ask.
    this.state.set(review.rows.length ? 'ready' : 'nothingToReview');
    this.rows.set(review.rows);
  }

  /**
   * Bulk choice for every row. A term sheet that supersedes the deal is the common
   * case, so "take everything" should not mean twenty clicks; rows whose current
   * value is empty stay on the document either way, since there is nothing to keep.
   */
  selectAll(selection: FieldSelection): void {
    this.rows.update(rows =>
      rows.map(row => ({
        ...row,
        selection: row.isCurrentEmpty ? 'document' : selection,
      }))
    );
  }

  select(key: string, selection: FieldSelection): void {
    this.rows.update(rows =>
      rows.map(row => (row.key === key ? { ...row, selection } : row))
    );
  }

  confirm(): void {
    const chosen = this.rows()
      .filter(row => row.selection === 'document')
      .reduce<ExtractionReviewResult>((acc, row) => {
        acc[row.key] = parseFieldValue(row.documentRaw, row.type);
        return acc;
      }, {});
    this.dialogRef.close(chosen);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
